import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", price: 0, stock: 0, sku: "", active: true, iva_included: true };

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  async function load() {
    const { data } = await api.get("/products");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm(empty); setOpen(true); }
  function openEdit(p) { setEditing(p); setForm(p); setOpen(true); }

  async function save() {
    try {
      const payload = { ...form, price: Number(form.price), stock: Number(form.stock), iva_included: form.iva_included !== false };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Guardado");
      setOpen(false);
      load();
    } catch { toast.error("Error al guardar"); }
  }

  async function remove(id) {
    if (!confirm("¿Eliminar producto?")) return;
    await api.delete(`/products/${id}`);
    load();
  }

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Inventario</p>
          <h1 className="font-display text-4xl font-light tracking-tight mt-3">Productos</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="products-new-btn"><Plus className="h-4 w-4 mr-2" /> Nuevo producto</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar producto" : "Nuevo producto"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Precio</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" /></div>
                <div className="space-y-2"><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock-input" /></div>
              </div>
              <div className="space-y-2"><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku-input" /></div>
              <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                <div>
                  <Label htmlFor="iva-prod" className="cursor-pointer">Precio incluye IVA (16%)</Label>
                  <p className="text-[11px] text-slate-500 mt-0.5">Desactiva si el producto está exento o factura por separado.</p>
                </div>
                <Switch id="iva-prod" checked={form.iva_included !== false} onCheckedChange={(v) => setForm({ ...form, iva_included: v })} data-testid="product-iva-switch" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} data-testid="product-save-btn">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table data-testid="products-table">
            <TableHeader>
              <TableRow>
                <TableHead>Producto</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Precio</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-slate-500">{p.sku}</TableCell>
                  <TableCell>{p.stock}</TableCell>
                  <TableCell>
                    <div>${p.price.toFixed(2)}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">{p.iva_included !== false ? "IVA incluido" : "Sin IVA"}</div>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`}><Pencil className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`product-delete-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
